<template>
  <div class="container mt-4">
    <h1>📝 TextareaField</h1>
    <p>
      A flexible Vue 3 textarea component with <code>v-model</code>, labeling, auto-focus,
      validation error display, and accessibility support.
    </p>

    <hr />

    <h2>✨ Features</h2>
    <ul>
      <li>🔄 Two-way binding via <code>v-model</code></li>
      <li>🏷 Optional label and required indicator</li>
      <li>⚡ Auto-focus support</li>
      <li>⚠️ Built-in error display</li>
      <li>📏 Configurable number of <code>rows</code></li>
      <li>🚫 Disabled or read-only states</li>
      <li>🆔 Unique ID for accessibility</li>
    </ul>

    <hr />

    <h2>📦 Props</h2>
    <ul>
      <li><b>modelValue</b> (String, default: "") — Bound value</li>
      <li><b>label</b> (String) — Label text</li>
      <li><b>placeholder</b> (String) — Placeholder text</li>
      <li><b>rows</b> (Number, default: 4) — Number of rows</li>
      <li><b>disabled</b> (Boolean, default: false) — Disables textarea</li>
      <li><b>readonly</b> (Boolean, default: false) — Read-only mode</li>
      <li><b>autoFocus</b> (Boolean, default: false) — Focus on mount</li>
      <li><b>required</b> (Boolean, default: false) — UI required indicator</li>
      <li><b>error</b> (String) — Error message text</li>
    </ul>

    <hr />

    <h2>📡 Emits</h2>
    <ul>
      <li><b>update:modelValue</b> — Emits textarea value on input</li>
      <li><b>focus</b> — Emits when textarea is focused</li>
      <li><b>blur</b> — Emits when textarea loses focus</li>
    </ul>

    <hr />

    <h2>📌 Live Example: Basic</h2>
    <div style="max-width: 400px;">
      <textarea-field
          v-model="textBasic"
          label="Description"
          placeholder="Type something..."
      />
      <p>Value: {{ textBasic }}</p>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;textarea-field
        v-model="textBasic"
        label="Description"
        placeholder="Type something..."
      /&gt;

      data() {
        return {
          textBasic: ""
        }
      }
    </code></pre>

    <hr />

    <h2>📌 Live Example: With Validation</h2>
    <div style="max-width: 400px;">
      <textarea-field
          v-model="textError"
          label="Notes"
          required
          :error="!textError ? 'This field is required' : ''"
      />
      <p>Value: {{ textError }}</p>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;textarea-field
        v-model="textError"
        label="Notes"
        required
        :error="!textError ? 'This field is required' : ''"
      /&gt;

      data() {
        return {
          textError: ""
        }
      }
    </code></pre>

    <hr />

    <h2>📌 Live Example: AutoFocus & Rows</h2>
    <div style="max-width: 400px;">
      <textarea-field
          v-model="textFocus"
          label="Auto Focus"
          :rows="6"
          :autoFocus="true"
      />
      <p>Value: {{ textFocus }}</p>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;textarea-field
        v-model="textFocus"
        label="Auto Focus"
        rows="6"
        :autoFocus="true"
      /&gt;

      data() {
        return {
          textFocus: ""
        }
      }
    </code></pre>

    <hr />

    <h2>📌 Live Example: Disabled & Read-Only</h2>
    <div style="max-width: 400px;">
      <textarea-field
          v-model="textDisabled"
          label="Disabled"
          placeholder="You cannot type here"
          :disabled="true"
      />
      <p>Value: {{ textDisabled }}</p>

      <textarea-field
          v-model="textReadonly"
          label="Read-Only"
          placeholder="This is read-only"
          :readonly="true"
      />
      <p>Value: {{ textReadonly }}</p>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;!-- Disabled Textarea --&gt;
      &lt;textarea-field
        v-model="textDisabled"
        label="Disabled"
        placeholder="You cannot type here"
        :disabled="true"
      /&gt;

      &lt;!-- Read-Only Textarea --&gt;
      &lt;textarea-field
        v-model="textReadonly"
        label="Read-Only"
        placeholder="This is read-only"
        :readonly="true"
      /&gt;

      data() {
        return {
          textDisabled: "Cannot edit this",
          textReadonly: "Read-only content"
        }
      }
    </code></pre>

    <h2>🧠 Behavior</h2>
    <ul>
      <li>Input changes are emitted immediately via <code>update:modelValue</code></li>
      <li>Auto-focus works on mount if <code>autoFocus</code> is true</li>
      <li>Supports disabled and read-only states</li>
      <li>Error messages are displayed below the textarea</li>
    </ul>

    <hr />

    <h2>✅ Notes</h2>
    <ul>
      <li>Unique <code>id</code> is generated automatically for accessibility if not provided</li>
      <li>Validation logic (required, min length, etc.) should be handled externally</li>
      <li>Events <code>focus</code> and <code>blur</code> are available for custom behavior</li>
    </ul>
  </div>
</template>

<script>
import TextareaField from "@/components/common/Textarea.vue";

export default {
  name: "TextareaFieldDocs",

  components: {
    TextareaField
  },

  data() {
    return {
      textBasic: "",
      textError: "",
      textFocus: "",
      textDisabled: "Cannot edit this",
      textReadonly: "Read-only content"
    };
  }
};
</script>